import { SerializeUtils } from './serialize.utils';

export interface KdfParams {
  iterations: number;
  hash: 'SHA-256' | 'SHA-384' | 'SHA-512';
}

export interface EncryptedBlob {
  ciphertext: string; // base64
  iv: string; // base64
  salt: string; // base64
  kdf: KdfParams;
}

export class EncryptionUtils {
  private static readonly textEncoder = new TextEncoder();
  private static readonly textDecoder = new TextDecoder();

  public static async encryptObject(
    obj: unknown,
    password: string
  ): Promise<EncryptedBlob> {
    const json = JSON.stringify(obj, SerializeUtils.replacer);
    return this.encryptString(json, password);
  }

  public static async decryptToObject<T = unknown>(
    encryptedBlob: EncryptedBlob,
    password: string
  ): Promise<T> {
    const json = await this.decryptToString(encryptedBlob, password);
    return JSON.parse(json, SerializeUtils.reviver) as T;
  }

  // Encrypt an arbitrary string payload (we'll JSON.stringify objects before calling this)
  public static async encryptString(
    plainText: string,
    password: string
  ): Promise<EncryptedBlob> {
    // generate a random salt and iv
    const salt = window.crypto.getRandomValues(new Uint8Array(16)); // store with ciphertext
    const iv = window.crypto.getRandomValues(new Uint8Array(12)); // AES-GCM 12-byte IV recommended

    const saltB64 = this.bufToBase64(salt.buffer);
    const ivB64 = this.bufToBase64(iv.buffer);

    const key = await this.deriveKeyFromPassword(password, saltB64);
    const ciphertext = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      this.textEncoder.encode(plainText)
    );

    return {
      ciphertext: this.bufToBase64(ciphertext),
      iv: ivB64,
      salt: saltB64,
      // include PBKDF2 params so decryptor can derive same key
      kdf: { iterations: 200_000, hash: 'SHA-256' },
    };
  }

  // Decrypt the structure returned by encryptString
  public static async decryptToString(
    encryptedBlob: EncryptedBlob,
    password: string
  ): Promise<string> {
    if (
      !encryptedBlob ||
      !encryptedBlob.ciphertext ||
      !encryptedBlob.iv ||
      !encryptedBlob.salt ||
      !encryptedBlob.kdf
    ) {
      return null;
    }

    try {
      const { ciphertext, iv, salt, kdf } = encryptedBlob;
      const key = await this.deriveKeyFromPassword(
        password,
        salt,
        kdf.iterations
      );
      const ivBuf = this.base64ToBuf(iv);
      const cipherBuf = this.base64ToBuf(ciphertext);
      const plainBuf = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: new Uint8Array(ivBuf) },
        key,
        cipherBuf
      );
      return this.textDecoder.decode(plainBuf);
    } catch (e: any) {
      return null;
    }
  }

  // Derive an AES-GCM key from a passphrase and salt using PBKDF2
  private static async deriveKeyFromPassword(
    password: string,
    saltBase64: string,
    iterations = 200_000
  ): Promise<CryptoKey> {
    const passwordKey = await window.crypto.subtle.importKey(
      'raw',
      this.textEncoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );
    const salt = new Uint8Array(this.base64ToBuf(saltBase64));
    const derivedKey = await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations,
        hash: 'SHA-256',
      },
      passwordKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
    return derivedKey;
  }

  private static bufToBase64(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf);
    const chunkSize = 0x8000; // safe chunk size
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i += chunkSize)
      // subarray returns Uint8Array; use fromCharCode on each chunk
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    return btoa(binary);
  }

  private static base64ToBuf(b64: string): ArrayBuffer {
    const binary = atob(b64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  }
}
